import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Pagination,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useSearchParams } from "react-router";
import { api } from "../lib/api.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow } from "../components/Neon.js";

interface PublicPost {
  id: string;
  text: string;
  mediaUrl: string | null;
  mediaType: string | null;
  platforms: string[];
  platformResults: Array<{ platform: string; url: string }>;
  publishedAt: string;
}

interface PostsResponse {
  data: PublicPost[];
  total: number;
  page: number;
  totalPages: number;
}

export function Posts() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") ?? 1);

  const { data, isLoading } = useQuery({
    queryKey: ["public", "posts", page],
    queryFn: () => api<PostsResponse>(`/v1/posts?page=${page}&limit=20`),
    placeholderData: (prev) => prev,
  });

  const posts = data?.data ?? [];

  return (
    <>
      <SEO
        title="Posts — Corian Park"
        description="Latest social posts from Corian Park — earthmoving operators, equipment, and projects across Victoria."
        canonical="https://corianpark.au/posts"
      />

      <Box sx={{ mb: 4 }}>
        <NeonEyebrow glow>SOCIAL FEED</NeonEyebrow>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: 38, md: 56 },
            fontWeight: 900,
            mt: 1,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
          }}
        >
          Posts
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 600 }}>
          Updates we've shared across Facebook, Instagram, X, TikTok and Google Business — all in
          one place.
        </Typography>
      </Box>

      {isLoading && !data && (
        <Stack spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={180} />
          ))}
        </Stack>
      )}

      {!isLoading && posts.length === 0 && (
        <Alert severity="info">No posts published yet.</Alert>
      )}

      <Stack spacing={2.5}>
        {posts.map((p) => (
          <Box
            key={p.id}
            sx={{
              p: 2.5,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {dayjs(p.publishedAt).format("D MMM YYYY · HH:mm")}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {p.platforms.map((pl) => (
                <Chip key={pl} size="small" variant="outlined" label={pl} />
              ))}
            </Stack>

            {p.mediaUrl && p.mediaType === "image" && (
              <Box
                component="img"
                src={p.mediaUrl}
                alt=""
                loading="lazy"
                sx={{
                  width: "100%",
                  maxHeight: 480,
                  objectFit: "cover",
                  borderRadius: 1,
                  mb: 2,
                  bgcolor: "action.hover",
                }}
              />
            )}
            {p.mediaUrl && p.mediaType === "video" && (
              <Box
                component="video"
                src={p.mediaUrl}
                controls
                playsInline
                sx={{
                  width: "100%",
                  maxHeight: 480,
                  borderRadius: 1,
                  mb: 2,
                  bgcolor: "common.black",
                }}
              />
            )}

            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
              {p.text}
            </Typography>

            {p.platformResults.length > 0 && (
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75, mt: 1.5 }}>
                {p.platformResults.map((pr) => (
                  <Chip
                    key={pr.platform}
                    size="small"
                    label={`View on ${pr.platform}`}
                    component="a"
                    href={pr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    sx={{ textDecoration: "none" }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>

      {data && data.totalPages > 1 && (
        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={data.totalPages}
            page={page}
            onChange={(_, p) => {
              const next = new URLSearchParams(params);
              next.set("page", String(p));
              setParams(next);
            }}
            color="primary"
          />
        </Box>
      )}
      {isLoading && data && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      )}
    </>
  );
}
